import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  User, ShieldCheck, Info, Target, 
  Cpu, ArrowRight, GraduationCap, 
  FileText, UserCheck, XCircle, 
  ChevronDown,
  ChevronRight,
  LogOut,
  X,
  Menu,
  Zap
} from "lucide-react";
import EvalLogo from "../Images/EvalLogo.png";

import step1 from "../Images/Step1new.png";
import step2 from "../Images/Step2.png";
import omnistep2 from "../Images/omniStep3.png";
import omnistep3 from "../Images/omniStep2New.png";
import omnistep4 from "../Images/omniStep4.png";

import step3 from "../Images/Step3.png";
import step4 from "../Images/Step4.png";
import step5 from "../Images/Step5New.png";
import step6 from "../Images/Step6.png";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";


const EvalHeader = () => {
  // --- States for Modals and Navigation ---

  const [showPaywall, setShowPaywall] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showUseCases, setShowUseCases] = useState(false);
  const [guideStep, setGuideStep] = useState(0);
  const [zoomImage, setZoomImage] = useState(null);
  const [now, setNow] = useState(new Date());
const [user, setUser] = useState(null);
const [isDropdownOpen, setIsDropdownOpen] = useState(false);

const [isOpen, setIsOpen] = useState(false);
const location = useLocation();

  const [userProfile, setUserProfile] = useState(null);

const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    const timer = setInterval(() => setNow(new Date()), 1000);
    
    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, []);


  // --- Clock Effect ---
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);


useEffect(() => {
  let unsubscribe = () => {};

  if (user) {
    const userRef = doc(db, "users", user.uid);

    // onSnapshot listens for real-time changes
    unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        console.log("Profile updated in real-time:", docSnap.data().credits_remaining);
        setUserProfile(docSnap.data());
      } else {
        console.log("No such user profile!");
      }
    }, (error) => {
      console.error("Error listening to profile:", error);
    });
  }

  // Cleanup: This stops the listener when the user logs out or leaves the page
  return () => unsubscribe();
}, [user]);

  const handleSignOut = async () => {
  setIsDropdownOpen(false);
  try {
    await auth.signOut();
    // Use navigate for a smoother SPA transition or window.location for full reset
    navigate("/"); 
  } catch (error) {
    console.error("Error signing out:", error);
  }
};
  // --- Mock Data (Replace with your actual SYSTEM_GUIDE array) ---
  const SYSTEM_GUIDE = [
    {
      title: "Standard Selection",
      subTitle: "Regulatory Framework",
      description:
        "Define the acceptance criteria by selecting the applicable inspection code (e.g., ASME, API, or ISO). This calibrates the evaluation logic for automatic flaw detection.",
      images: [{ label: "Eval NDT – Select Standard", src: step1 }],
      requirements: [
        "Confirm project specification",
        "Verify code edition year",
      ],
    },
    {
      title: "Configuration Sync",
      subTitle: "Setup File (CSV)",
      description:
        "Bridge the hardware settings with the analysis engine. Export the CSV from OmniPC to sync probe frequency, aperture, and wedge parameters.",
      images: [
        { label: "1. OmniPC – Export Setup", src: omnistep2 },
        { label: "2. Eval NDT – Import Setup", src: step2 },
      ],
      requirements: ["OmniPC v5.x or higher", "CSV encoding: UTF-8"],
    },
    {
      title: "Volumetric Mapping",
      subTitle: "C-Scan Data (TXT)",
      description:
        "Upload the Sectorial/C-Scan image data. This enables the volumetric reconstruction used for spatial visualization of indications.",
      images: [
        { label: "1. OmniPC – Export C-Scan", src: omnistep3 },
        { label: "2. Eval NDT – Import C-Scan", src: step3 },
      ],
      requirements: ["Export resolution: 1mm", "Sectorial view enabled"],
    },
    {
      title: "Signal Acquisition",
      subTitle: "A-Scan Data (TXT)",
      description:
        "Transfer raw amplitude and Time-of-Flight data. This is the primary source for defect sizing and signal-to-noise ratio calculation.",
      images: [
        { label: "1. OmniPC – Export A-Scan", src: omnistep4 },
        { label: "2. Eval NDT – Import A-Scan", src: step4 },
      ],
      requirements: ["Full waveform data", "Gate A/B tracking"],
    },
    {
      title: "Automated Evaluation",
      subTitle: "Logic Engine Processing",
      description:
        "Execute the 'Run New Scan' command. The system will parse all three data sources to identify and categorize ultrasonic indications.",
      images: [{ label: "Eval NDT – Processing Output", src: step5 }],
      requirements: [
        "Check file naming consistency",
        "Monitor processing progress",
      ],
    },
    {
      title: "Final Certification",
      subTitle: "Analysis & Reporting",
      description:
        "Validate the AI-detected indications against the selected standard and generate a secure, tamper-proof PDF inspection report.",
      images: [{ label: "Eval NDT – PDF Report Output", src: step6 }],
      requirements: ["Review C-Scan overlay", "Digital signature entry"],
    },
  ];

  return (
    <header className="sticky top-0 z-50 flex flex-col bg-white border-b border-slate-200 shadow-sm">
      {/* MAIN NAVIGATION BAR */}
      <nav className="h-16 md:h-20 flex items-center justify-between px-4 md:px-10 relative bg-white border-b border-slate-100 z-20">
       <div className="flex items-center gap-4 shrink-0 z-10">
    <Link
      to="/"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="flex items-center group active:scale-95 transition-all duration-200 select-none"
    >
      <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center group-hover:drop-shadow-md transition-all">
        <img
          src={EvalLogo}
          className="w-full h-full object-contain group-hover:-rotate-6 transition-transform"
          alt="Logo"
        />
      </div>
    </Link>

    {/* SUBTITLES SHIFTED HERE */}
  <div className="hidden lg:flex items-center border-l border-slate-200 pl-4 py-1 gap-4">
  {/* Left Column: Primary Systems */}
  <div className="flex flex-col">
    <div className="flex items-center gap-1.5">
      <div className="w-1 h-1 bg-slate-400 rounded-full" />
      <p className="text-[7px] md:text-[8px] font-bold text-slate-500 uppercase tracking-[0.15em] leading-none">
        Evaluation Engine
      </p>
    </div>
    <div className="flex items-center gap-1.5 mt-1.5">
      <div className="w-1 h-1 bg-slate-400 rounded-full" />
      <p className="text-[7px] md:text-[8px] font-bold text-slate-500 uppercase tracking-[0.15em] leading-none">
        Precision Core
      </p>
    </div>
  </div>

  {/* Separator Line */}
  <div className="h-6 w-[1px] bg-slate-200" />

  {/* Right Column: Live Status */}
  <div className="flex flex-col justify-center">
    <div className="flex items-center gap-2 px-2 py-1 bg-indigo-50 rounded border border-indigo-100/50">
      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse" />
      <p className="text-[7px] md:text-[8px] font-black text-indigo-600 uppercase tracking-[0.2em] leading-none">
        Real-Time Analytics
      </p>
    </div>
  </div>
</div>
  </div>

        {/* 2. CENTER: EVAL NDT + SUBTITLES */}
    <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center text-center pointer-events-none">
  {/* Top technical line */}
  <div className="w-12 h-[1px] bg-indigo-200 mb-1" />
  
  <div className="relative px-6">
    {/* Decorative corner brackets */}
    <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-slate-300" />
    <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-slate-300" />
    
    <h1 className="text-lg md:text-4xl font-black tracking-tighter text-slate-800 leading-none uppercase">
      EVAL<span className="text-indigo-600">NDT</span>
    </h1>

    <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-slate-300" />
    <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-slate-300" />
  </div>

  {/* Bottom status indicator */}
  <div className="flex items-center gap-2 mt-1">
    <span className="w-8 h-[1px] bg-slate-200" />
    <span className="text-[7px] font-black text-indigo-400 uppercase tracking-[0.3em]">Engine</span>
    <span className="w-8 h-[1px] bg-slate-200" />
  </div>
</div>

        {/* 3. RIGHT: AUTH & TIME */}
<div className="flex items-center gap-3 z-10">
    {/* TIME DISPLAY (Hidden on Mobile) */}
    <div className="hidden md:flex flex-col items-end leading-tight pr-4 border-r border-slate-200 mr-1">
      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Time</span>
      <span className="text-xs font-black text-slate-800 font-mono uppercase">
        {now.toLocaleTimeString("en-GB", { hour12: false, hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>

    {user ? (
      <div className="relative flex items-center gap-3">
        
       {/* CREDITS BADGE */}
<div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg shadow-sm transition-all duration-500 hover:bg-amber-100">
  <Zap size={12} className={`text-amber-600 fill-amber-600 ${userProfile?.credits_remaining ? 'animate-pulse' : ''}`} />
  <div className="flex flex-col leading-none">
    <span className="text-[8px] font-black text-amber-600 uppercase tracking-tight">Credits</span>
    <span className="text-[11px] font-black text-slate-800 tabular-nums">
      {userProfile?.credits_remaining ?? 0}
    </span>
  </div>
</div>

        {/* USER AVATAR & NAME BUTTON */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`flex items-center gap-3 p-1 pr-3 rounded-full border transition-all active:scale-95 group shadow-sm
            ${isDropdownOpen 
              ? "bg-indigo-50 border-indigo-200 ring-2 ring-indigo-100" 
              : "bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-300"
            }`}
        >
          <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-white text-[11px] font-black border-2 border-white shadow-sm overflow-hidden shrink-0">
            {user.photoURL ? (
              <img src={user.photoURL} alt="User" className="w-full h-full object-cover" />
            ) : (
              user.email.charAt(0).toUpperCase()
            )}
          </div>
          
          <div className="hidden sm:flex flex-col items-start leading-none">
            <span className="text-[8px] font-black text-indigo-600 uppercase tracking-widest mb-0.5">Authorized</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-black text-slate-800 truncate max-w-[90px]">
                {user.displayName || user.email.split('@')[0]}
              </span>
              <ChevronDown 
                size={12} 
                className={`text-slate-400 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180 text-indigo-600' : ''}`} 
              />
            </div>
          </div>
        </button>

        {/* DROPDOWN MENU */}
        {isDropdownOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
            
            <div className="absolute right-0 top-full mt-3 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl shadow-indigo-200/40 p-2 z-50 flex flex-col gap-1 animate-in fade-in slide-in-from-top-2 duration-200">
              
              {/* DROPDOWN HEADER */}
              <div className="px-4 py-3 bg-slate-50/50 rounded-xl mb-1 border border-slate-100">
                <p className="text-[9px] font-black text-indigo-500 uppercase tracking-[0.15em]">Personal Session</p>
                <p className="text-[11px] font-bold text-slate-800 truncate mt-0.5 mb-2">{user.email}</p>
                
                {/* Credit Progress Bar */}
                <div className="space-y-1.5 mt-2 pt-2 border-t border-slate-200/60">
                  <div className="flex justify-between items-center">
                    <span className="text-[8px] font-black text-slate-400 uppercase">Usage</span>
                    <span className="text-[10px] font-black text-indigo-600">{userProfile?.credits_remaining ?? 0}/10 Reports</span>
                  </div>
                  <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-600 transition-all duration-500" 
                      style={{ width: `${Math.min(((userProfile?.credits_remaining ?? 0) / 10) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* NAVIGATION LINKS */}
              <Link
                to="/dashboard/account"
                onClick={() => setIsDropdownOpen(false)}
                className="flex items-center justify-between px-4 py-2.5 rounded-xl hover:bg-slate-50 text-slate-700 transition-all group/item"
              >
                <div className="flex items-center gap-3">
                  <User size={14} className="text-slate-400 group-hover/item:text-indigo-600" />
                  <span className="text-[11px] font-black uppercase tracking-wide">My Account</span>
                </div>
                <ChevronRight size={12} className="text-slate-300" />
              </Link>

              <Link
                to="/dashboard"
                state={{ triggerRefresh: Date.now() }}
                onClick={() => setIsDropdownOpen(false)}
                
                className="flex items-center justify-between px-4 py-2.5 rounded-xl hover:bg-slate-50 text-slate-700 transition-all group/item"
              >
                <div className="flex items-center gap-3">
                  <Target size={14} className="text-slate-400 group-hover/item:text-indigo-600" />
                  <span className="text-[11px] font-black uppercase tracking-wide">Dashboard</span>
                </div>
                <ChevronRight size={12} className="text-slate-300" />
              </Link>

              <div className="h-px bg-slate-100 mx-2 my-1" />

              {/* SIGN OUT BUTTON */}
              <button
                onClick={handleSignOut}
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-red-50 text-red-600 transition-all w-full group/logout"
              >
                <LogOut size={14} className="group-hover/logout:-translate-x-1 transition-transform" />
                <span className="text-[11px] font-black uppercase tracking-wide text-left">Sign Out</span>
              </button>
            </div>
          </>
        )}
      </div>
    ) : (
      <Link
        to="/login"
        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-all font-black text-[11px] uppercase tracking-[0.1em] shadow-lg active:scale-95"
      >
        <UserCheck size={16} />
        <span>Login</span>
      </Link>
    )}
  </div>
      </nav>

      {/* SUB-HEADER: STATUS & UTILITIES */}
<div className="h-10 bg-slate-50 border-b border-slate-200 px-4 md:px-10 flex items-center justify-between overflow-x-auto no-scrollbar">
  {/* LEFT: SYSTEM STATUS (Shrink-0 keeps it from squishing) */}
  <div className="flex items-center gap-6 shrink-0">
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
      <span className="text-[10px] font-black text-slate-600 uppercase tracking-wider">
        System: <span className="text-emerald-600">Calibrated</span>
      </span>
    </div>
    <span className="hidden md:block h-4 w-[1px] bg-slate-300" />
    <div className="hidden md:flex items-center gap-2">
      <ShieldCheck size={12} className="text-slate-400" />
      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">ISO 9001:2015 Compliant</span>
    </div>
  </div>

  {/* CENTER: SPREAD NAVIGATION MENU (Flex-1 and Justify-Center spreads it perfectly) */}
<div className="flex flex-1 items-center justify-end lg:justify-center px-4 lg:px-10">
  {/* MOBILE TOGGLE (Hidden on Desktop) */}
  <button 
    onClick={() => setIsOpen(!isOpen)} 
    className="lg:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
  >
    {isOpen ? <X size={20} /> : <Menu size={20} />}
  </button>

  {/* DESKTOP MENU */}
  <div className="hidden lg:flex items-center gap-8">
    {[
      { name: 'Engine', path: '/engine' },
      { name: 'Home', path: '/home' },
      { name: 'About', path: '/about' },
      { name: 'Enquiry', path: '/contact' },
    ].map((link, index, array) => (
      <React.Fragment key={link.path}>
        <NavLink
          to={link.path}
          className={({ isActive }) => 
            `text-[10px] font-black uppercase tracking-[0.2em] transition-all relative py-1
            ${isActive || (link.path === '/engine' && location.pathname === "/") 
              ? "text-[#1E3A8A] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-[#F97316]" 
              : "text-slate-400 hover:text-slate-900"}`
          }
        >
          {link.name}
        </NavLink>
        {index < array.length - 1 && (
          <span className="text-slate-200 text-[10px] font-light select-none">/</span>
        )}
      </React.Fragment>
    ))}
  </div>

  {/* MOBILE DROPDOWN PANEL */}
  <div className={`
    fixed top-[64px] left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 ease-in-out z-50 lg:hidden overflow-hidden
    ${isOpen ? "max-h-80 opacity-100 visible" : "max-h-0 opacity-0 invisible"}
  `}>
    <div className="flex flex-col p-6 gap-6 shadow-xl">
      {[
        { name: 'Engine', path: '/engine' },
        { name: 'Home', path: '/home' },
        { name: 'About', path: '/about' },
        { name: 'Enquiry', path: '/contact' },
      ].map((link) => (
        <NavLink
          key={link.path}
          to={link.path}
          onClick={() => setIsOpen(false)}
          className={({ isActive }) => 
            `text-[11px] font-black uppercase tracking-[0.2em] transition-all 
            ${isActive ? "text-[#F97316]" : "text-slate-500"}`
          }
        >
          {link.name}
        </NavLink>
      ))}
    </div>
  </div>
</div>

  {/* RIGHT: UTILITY BUTTONS (Shrink-0 keeps it from squishing) */}
  <div className="flex items-center gap-2 md:gap-4 shrink-0">
    <button onClick={() => setShowGuide(true)} className="flex items-center gap-2 px-3 py-1 hover:text-indigo-600 transition-colors text-slate-500 font-black text-[9px] uppercase tracking-widest">
      <Info size={12} /> Guide
    </button>
    <button onClick={() => setShowUseCases(true)} className="flex items-center gap-2 px-3 py-1 bg-white border border-slate-200 rounded-md shadow-sm hover:border-indigo-300 transition-all text-slate-700 font-black text-[9px] uppercase tracking-widest">
      <Target size={12} className="text-indigo-500" /> Use Cases
    </button>
  </div>
</div>

      {/* MOBILE STATUS RIBBON */}
      {/* <div className="flex md:hidden items-center justify-between px-4 py-1 bg-slate-50 border-t border-slate-100">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Calibrated</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" className="flex items-center px-3 py-1.5 rounded-lg bg-indigo-600 text-white active:scale-95 text-[9px] font-black uppercase tracking-widest">Login</Link>
          <button onClick={() => setShowGuide(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 active:scale-95"><Info size={14} /></button>
          <span className="text-[10px] font-black text-slate-700 font-mono">{now.toLocaleTimeString("en-GB", { hour12: false, hour: '2-digit', minute: '2-digit' })}</span>
          <span className="text-[9px] font-bold text-slate-400 uppercase">{now.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
        </div>
      </div> */}

      {/* PAYWALL MODAL */}
{showPaywall && (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300">
    <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full overflow-hidden border border-slate-100">
      <div className="p-12 text-center">
        
        {/* Larger, High-Contrast Icon */}
        <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-inner">
          <Cpu size={40} strokeWidth={2.5} />
        </div>
        
        {/* Bold, Highly Readable Typography */}
        <h2 className="text-3xl font-black text-slate-800 mb-4 tracking-tight leading-tight">
          Sign in to Continue
        </h2>
        <p className="text-slate-500 text-base leading-relaxed mb-10 px-2">
          Unlock <span className="font-bold text-slate-800">unlimited reports</span> and professional analysis tools by joining your secure workspace.
        </p>

        {/* High-Impact Actions */}
        <div className="flex flex-col gap-5">
          <button 
            onClick={() => window.location.href = '/login'} 
            className="group w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-sm tracking-[0.1em] hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 active:scale-95"
          >
            LOG IN NOW
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
          
          <button 
            onClick={() => setShowPaywall(false)}
            className="text-slate-400 text-xs font-black uppercase tracking-[0.25em] hover:text-slate-600 transition-colors py-2"
          >
            Return to Setup
          </button>
        </div>

        {/* Clean Professional Footer */}
        <div className="mt-10 pt-8 border-t border-slate-50">
          <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2">
            <ShieldCheck size={14} className="text-emerald-500" /> Secure Professional Access
          </p>
        </div>

      </div>
    </div>
  </div>
)}

      {/* SYSTEM GUIDE MODAL */}
        {showGuide && (
          <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-0 md:p-4">
            <div className="bg-white w-full h-full md:h-[85vh] md:max-h-[820px] md:w-[1200px] md:rounded-2xl shadow-2xl flex flex-col md:flex-row overflow-hidden border border-slate-200">

              {/* MOBILE MODAL HEADER */}
              <div className="flex md:hidden p-4 border-b bg-white justify-between items-center">
                <h2 className="font-black text-slate-800 text-sm uppercase tracking-tight">Inspection Procedure</h2>
                <button onClick={() => setShowGuide(false)} className="p-2 text-slate-400">✕</button>
              </div>

              {/* LEFT – PROCEDURE STEPS */}
              <aside className="w-full md:w-80 bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 p-4 md:p-6 flex flex-col shrink-0">
                <div className="hidden md:block mb-8">
                  <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Procedure Guide</span>
                  <h2 className="text-lg font-black text-slate-800">Inspection Steps</h2>
                </div>

                <div className="flex md:flex-col gap-3 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
                  {SYSTEM_GUIDE.map((step, idx) => (
                    <button
                      key={idx}
                      onClick={() => setGuideStep(idx)}
                      className={`flex-shrink-0 w-[140px] md:w-full text-left p-3 md:p-4 rounded-xl transition-all border ${guideStep === idx
                          ? "bg-white border-indigo-600 shadow-sm"
                          : "border-transparent hover:bg-slate-100"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-[10px] w-5 h-5 rounded flex items-center justify-center font-black transition-colors ${guideStep === idx ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"
                          }`}>
                          {idx + 1}
                        </span>
                        <div className="truncate">
                          <p className={`text-xs font-bold truncate ${guideStep === idx ? "text-slate-900" : "text-slate-600"}`}>
                            {step.title}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </aside>

              {/* RIGHT – CONTENT */}
              <section className="flex-1 flex flex-col min-h-0 bg-white">
                <header className="hidden md:flex p-8 border-b border-slate-200 justify-between items-start">
                  <div>
                    <h1 className="text-2xl font-black text-slate-800 tracking-tight">{SYSTEM_GUIDE[guideStep].title}</h1>
                    <p className="text-[11px] font-bold text-indigo-600 uppercase tracking-widest mt-1">{SYSTEM_GUIDE[guideStep].subTitle}</p>
                  </div>
                  <button onClick={() => setShowGuide(false)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">✕</button>
                </header>

                <div className="flex-1 px-4 md:px-8 py-6 overflow-y-auto">
                  <p className="text-slate-600 text-sm leading-relaxed mb-8 max-w-2xl">{SYSTEM_GUIDE[guideStep].description}</p>

                  <div className={`grid gap-4 mb-10 ${SYSTEM_GUIDE[guideStep].images.length > 1 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
                    {SYSTEM_GUIDE[guideStep].images.map((img, i) => (
                      <div key={i} className="space-y-2">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{img.label}</p>
                        <div onClick={() => setZoomImage(img)} className="bg-slate-50 border border-slate-200 rounded-xl p-2 aspect-video cursor-zoom-in relative group overflow-hidden">
                          <img src={img.src} alt={img.label} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                          <div className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/5 transition-colors flex items-center justify-center">
                            <span className="bg-white px-3 py-1 rounded-full text-[9px] font-bold text-indigo-600 opacity-0 group-hover:opacity-100 shadow-sm transition-opacity">View Detail</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 md:p-6">
                    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 mb-4">Acceptance Requirements</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SYSTEM_GUIDE[guideStep].requirements.map((req, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-slate-600 font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0" />
                          {req}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <footer className="p-4 md:p-6 border-t bg-slate-50 flex justify-between gap-4">
                  <button
                    disabled={guideStep === 0}
                    onClick={() => setGuideStep((s) => s - 1)}
                    className="flex-1 md:flex-none px-4 md:px-6 py-3 rounded-xl text-[10px] font-black text-slate-500 hover:bg-slate-200 disabled:opacity-30 uppercase tracking-widest transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => guideStep === SYSTEM_GUIDE.length - 1 ? setShowGuide(false) : setGuideStep((s) => s + 1)}
                    className="flex-1 md:flex-none px-6 md:px-10 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black hover:bg-indigo-600 uppercase tracking-widest transition-all shadow-md shadow-slate-200"
                  >
                    {guideStep === SYSTEM_GUIDE.length - 1 ? "Begin Evaluation" : "Next Step"}
                  </button>
                </footer>
              </section>
            </div>
          </div>
        )}

      {/* USE CASES MODAL */}
{showUseCases && (
  <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
    {/* MODAL CONTAINER: Responsive width and height */}
    <div className="bg-white w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">

      {/* HEADER: Condensed on mobile */}
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b bg-indigo-600 shrink-0">
        <div>
          <h2 className="text-base sm:text-lg font-black text-white tracking-tight">
            Eval<span className="text-indigo-200">NDT</span> Productivity Suite
          </h2>
          <p className="text-[9px] sm:text-[11px] font-bold text-indigo-100 uppercase tracking-widest">
            Inspection Acceleration & Training Framework
          </p>
        </div>
        <button
          onClick={() => setShowUseCases(false)}
          className="p-2 rounded-lg hover:bg-indigo-700 text-white font-black transition-colors"
          aria-label="Close modal"
        >
          ✕
        </button>
      </div>

      {/* CONTENT AREA: Scrollable with padding adjustments */}
      <div className="px-4 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6 bg-white overflow-y-auto">

        {/* CORE UTILITY & WORKFLOW: Stacks on mobile, 3-col on lg */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <section className="lg:col-span-2 flex flex-col justify-center bg-indigo-50/50 p-4 sm:p-6 rounded-2xl border border-indigo-100">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mb-2">
              The Speed Advantage
            </h3>
            <p className="text-sm text-slate-700 leading-relaxed font-medium">
              EvalNDT is an auxiliary tool designed to 
              <span className="text-indigo-600 font-bold"> accelerate the post-inspection workflow.</span> It eliminates manual data entry, allowing inspectors to move from raw data to a draft report in minutes.
            </p>
          </section>

          {/* LEVEL III VALIDATION CARD */}
          <section className="bg-amber-50 border border-amber-200 p-4 sm:p-6 rounded-2xl">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-2">
              Review Workflow
            </h3>
            <p className="text-[11px] text-amber-900 font-bold leading-tight">
              All generated reports are considered "Draft" until verified. Final acceptance/rejection must be decided by a certified Level III professional.
            </p>
          </section>
        </div>

        {/* APPLICATIONS GRID: 1 col (mobile) -> 2 col (tablet) -> 3 col (desktop) */}
        <section>
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 text-center md:text-left">
            Core Functional Modules
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Module 1 */}
            <div className="group hover:bg-indigo-600 p-5 rounded-2xl border border-slate-100 bg-slate-50 transition-all cursor-default">
              <GraduationCap className="text-indigo-600 group-hover:text-white mb-3" size={24} />
              <h4 className="text-xs font-black text-slate-800 group-hover:text-white mb-2 uppercase">Training & Simulation</h4>
              <p className="text-[11px] text-slate-500 group-hover:text-indigo-100 leading-tight">Ideal for training centers to simulate evaluation scenarios without expensive hardware.</p>
            </div>

            {/* Module 2 */}
            <div className="group hover:bg-indigo-600 p-5 rounded-2xl border border-slate-100 bg-slate-50 transition-all cursor-default">
              <FileText className="text-indigo-600 group-hover:text-white mb-3" size={24} />
              <h4 className="text-xs font-black text-slate-800 group-hover:text-white mb-2 uppercase">Draft Generation</h4>
              <p className="text-[11px] text-slate-500 group-hover:text-indigo-100 leading-tight">Automated PDF drafting for ISO-compliant structures, ready for final review.</p>
            </div>

            {/* Module 3 */}
            <div className="group hover:bg-indigo-600 p-5 rounded-2xl border border-slate-100 bg-slate-50 transition-all cursor-default sm:col-span-2 lg:col-span-1">
              <UserCheck className="text-indigo-600 group-hover:text-white mb-3" size={24} />
              <h4 className="text-xs font-black text-slate-800 group-hover:text-white mb-2 uppercase">Level III Gateway</h4>
              <p className="text-[11px] text-slate-500 group-hover:text-indigo-100 leading-tight">Digital review portal for Level III signatures and final engineering decisions.</p>
            </div>
          </div>
        </section>

        {/* SYSTEM LIMITS & POLICY: Stacks vertically on mobile */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border-2 border-dashed border-slate-200 rounded-2xl p-4 sm:p-5">
            <h4 className="text-[10px] font-black text-slate-400 uppercase mb-3">System Limitations</h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-xs font-bold text-slate-600">
                <XCircle size={14} className="text-red-500 shrink-0" /> No Auto-Accept / Reject
              </li>
              <li className="flex items-center gap-2 text-xs font-bold text-slate-600">
                <XCircle size={14} className="text-red-500 shrink-0" /> No Instrument Replacement
              </li>
            </ul>
          </div>

          <div className="bg-slate-900 rounded-2xl p-4 sm:p-5 flex flex-col justify-center">
            <h4 className="text-[10px] font-black text-indigo-400 uppercase mb-2 tracking-widest">Official Policy</h4>
            <p className="text-[10px] text-slate-400 leading-relaxed uppercase font-mono">
              The user retains 100% responsibility for diagnostic accuracy. EvalNDT is a data visualization and reporting layer only.
            </p>
          </div>
        </section>
      </div>

      {/* FOOTER: Flex wrapping for narrow screens */}
      <div className="px-4 py-4 sm:px-6 sm:py-5 border-t bg-slate-50 flex flex-col sm:flex-row gap-4 justify-between items-center shrink-0">
        <div className="flex items-center gap-1.5 order-2 sm:order-1">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[9px] font-black text-slate-500 uppercase">Engine Ready</span>
        </div>
        
        <button
          onClick={() => setShowUseCases(false)}
          className="w-full sm:w-auto px-8 py-3 bg-slate-900 text-white rounded-xl 
                     text-[10px] font-black uppercase tracking-widest
                     hover:bg-indigo-600 transition-all shadow-lg active:scale-95 order-1 sm:order-2"
        >
          Initialize Engine
        </button>
      </div>

    </div>
  </div>
)}
    </header>
  );
};

export default EvalHeader;